import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Heart, Loader2 } from 'lucide-react';
import { ProductCard } from '../../components/shopper/ProductCard';
import { ShopperNavActions } from '../../components/shopper/ShopperNavActions';
import { ShopperBottomNav } from '../../components/shopper/ShopperBottomNav';
import { BackButton } from '../../components/ui/BackButton';
import { SHOPPER_HOME } from '../../constants/routes';
import { useFavoritesContext } from '../../hooks/useFavorites';

export function LikesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const favorites = useFavoritesContext();

  const handleProductClick = (id: string) => {
    navigate(`/product/${id}`, { state: { from: location.pathname } });
  };

  // Favorites don't carry live size/stock info, so open the product page
  // where the shopper can pick an in-stock size.
  const handleAddToCart = (productId: string) => {
    navigate(`/product/${productId}`, { state: { from: location.pathname } });
  };

  return (
    <div className="min-h-screen bg-white pb-20 md:pb-0">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <BackButton fallback={SHOPPER_HOME} />
            <h1 className="text-xl font-serif text-heading">My Likes</h1>
            <ShopperNavActions className="p-2 hover:bg-gray-100 rounded-full transition-colors" iconClassName="h-6 w-6 text-heading" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {favorites.loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Loader2 className="h-8 w-8 text-primary animate-spin mx-auto mb-3" />
              <p className="text-sm text-body">Loading your likes...</p>
            </div>
          </div>
        ) : favorites.items.length === 0 ? (
          <div className="text-center py-20">
            <Heart className="h-16 w-16 text-gray-200 mx-auto mb-4" />
            <h2 className="text-xl font-serif text-heading mb-2">No likes yet</h2>
            <p className="text-sm text-body mb-6 max-w-md mx-auto">
              Tap the heart on any product to save it here. Your likes will be saved to your account when you sign in.
            </p>
            <button
              onClick={() => navigate(SHOPPER_HOME)}
              className="inline-block text-sm font-medium text-white bg-gray-900 px-6 py-2.5 rounded-full hover:bg-gray-800 transition-colors"
            >
              Start Browsing
            </button>
          </div>
        ) : (
          <>
            <p className="text-sm text-body mb-4">
              {favorites.favoriteCount} item{favorites.favoriteCount !== 1 ? 's' : ''}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 md:gap-6 pb-8">
              {favorites.items.map((item) => (
                <div
                  key={item.productId}
                  onClick={() => handleProductClick(item.productId)}
                  className="cursor-pointer"
                >
                  <ProductCard
                    product={{
                      id: item.productId,
                      name: item.name,
                      brand: item.brand,
                      price: item.price,
                      imageUrl: item.imageUrl,
                    }}
                    onAddToCart={(_, e) => {
                      e?.stopPropagation?.();
                      handleAddToCart(item.productId);
                    }}
                    onFavorite={() =>
                      favorites.toggleFavorite({
                        productId: item.productId,
                        name: item.name,
                        brand: item.brand,
                        price: item.price,
                        imageUrl: item.imageUrl,
                      })
                    }
                    isFavorited={true}
                    compact
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </main>

      <ShopperBottomNav />
    </div>
  );
}
